import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addPdfFinalText, drawPdfHeader, getWhitelabelSettings, hexToRgb, loadImageFromUrl } from './whitelabelPdf';

/**
 * Generate a branded PDF with a data table
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.title - Main title for the PDF
 * @param {string} [options.subtitle] - Optional subtitle
 * @param {Array<string>} options.columns - Array of column headers
 * @param {Array<Array<any>>} options.data - 2D array of table data
 * @param {string} options.filename - Filename for the PDF download
 * @param {Object} [options.filters] - Optional filters to display (key-value pairs)
 * @param {string} [options.dateLabel] - Optional label for the date (e.g., "As of Date")
 * @param {string} [options.date] - Optional date string to display
 * @param {Object} [options.tableStyles] - Optional custom table styles
 */
export const generateDataTablePDF = async (options) => {
  const {
    title,
    subtitle,
    columns,
    data,
    filename,
    filters = {},
    dateLabel,
    date,
    tableStyles = {},
    whitelabel = null,
    whitelabelExtraText = ''
  } = options;

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let y = 0;
  const whitelabelSettings = whitelabel ? getWhitelabelSettings({ data: { whitelabel } }) : null;
  const primaryColor = whitelabelSettings?.primary_color || '#2E5BFF';
  const primaryRgb = hexToRgb(primaryColor);
  const logoImg = whitelabelSettings?.logo_url ? await loadImageFromUrl(whitelabelSettings.logo_url) : null;
  const brandName = whitelabelSettings?.firm_name || 'SharpeMetrix';

  // HEADER (blue bar with white logo and title)
  await drawPdfHeader(pdf, {
    pageWidth,
    title,
    logoImg,
    logoText: whitelabelSettings?.firm_name || 'SharpeMetrix',
    primaryColor,
  });
  
  y = 40;

  // SUBTITLE (if provided)
  if (subtitle) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(whitelabelSettings ? `${brandName} Model Portfolio Performance Metrics` : subtitle, 15, y);
    y += 8;
  }

  // DATE (if provided)
  if (date) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const dateText = dateLabel ? `${dateLabel}: ${date}` : date;
    pdf.text(dateText, 15, y);
    y += 8;
  }

  // FILTERS SECTION (if provided)
  if (Object.keys(filters).length > 0) {
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...primaryRgb);
    pdf.text('Applied Filters', 15, y);
    y += 6;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    
    const filterEntries = Object.entries(filters);
    const maxFiltersPerPage = 15;
    let filterCount = 0;
    
    for (const [key, value] of filterEntries) {
      if (filterCount >= maxFiltersPerPage && y > pageHeight - 60) {
        pdf.addPage();
        y = 25;
        filterCount = 0;
      }
      
      if (value && value !== '' && value !== null && value !== undefined) {
        const filterText = `${key}: ${value}`;
        pdf.text(filterText, 15, y);
        y += 5;
        filterCount++;
      }
    }
    
    y += 5;
  }

  // Check if we need a new page
  if (y > pageHeight - 80) {
    pdf.addPage();
    y = 25;
  }

  // DATA TABLE
  const defaultTableStyles = {
    fontSize: 9,
    cellPadding: 3,
    font: 'helvetica',
    textColor: [40, 40, 40],
    lineColor: [220, 220, 220],
    lineWidth: 0.5,
    ...tableStyles
  };

  autoTable(pdf, {
    startY: y,
    head: [columns],
    body: data,
    theme: 'striped',
    styles: defaultTableStyles,
    headStyles: { 
      fillColor: primaryRgb, 
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10
    },
    alternateRowStyles: {
      fillColor: [248, 249, 250]
    },
    margin: { left: 15, right: 15 },
    pageBreak: 'auto',
    didDrawPage: (data) => {
      // Add page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      pdf.text(
        `Page ${currentPage} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }
  });

  // Add footer with disclaimer on last page
  const finalY = pdf.lastAutoTable.finalY;
  if (finalY < pageHeight - 40) {
    y = finalY + 15;
  } else {
    pdf.addPage();
    y = 25;
  }

  pdf.setFontSize(8);
  pdf.setTextColor(100, 100, 100);
  pdf.setFont('helvetica', 'italic');
  const disclaimerText = whitelabelSettings
    ? `This report is generated by ${brandName}. Past performance is not indicative of future results. Always seek advice from a Compliance professional when using ${brandName}.`
    : 'This report is generated by SharpeMetrix. Past performance is not indicative of future results. Always seek advice from a Compliance professional when using SharpeMetrix.';
  const disclaimerLines = pdf.splitTextToSize(disclaimerText, pageWidth - 30);
  pdf.text(disclaimerLines, 15, y);

  addPdfFinalText(pdf, {
    pageWidth,
    pageHeight,
    text: whitelabelSettings ? (whitelabelExtraText || whitelabelSettings.disclosure_text) : '',
    primaryColor,
  });

  // Save the PDF
  pdf.save(filename);
};

/**
 * Format a date for display in PDFs
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatPDFDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

/**
 * Format a number for PDF display
 * @param {number} value - Number to format
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatPDFNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return Number(value).toFixed(decimals);
};

/**
 * Format a currency value for PDF display
 * @param {number} value - Currency value to format
 * @returns {string} Formatted currency string
 */
export const formatPDFCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Format a percentage for PDF display
 * @param {number} value - Percentage value to format
 * @param {number} [decimals=1] - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPDFPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${Number(value).toFixed(decimals)}%`;
};

